import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  UseEditableTableStateParams,
  UseEditableTableStateReturn,
} from "../types";

/**
 * Hook for managing editable table state with change tracking across pagination
 *
 * Architecture:
 * 1. editMap - Stores edits (deltas) by row key
 * 2. originalDataMap - Stores original data for all edited rows (persists across pages)
 * 3. savedUpdates - Stores successfully saved rows temporarily (until fresh data arrives)
 * 4. currentData - Computed from initialData (current page) + savedUpdates + editMap
 *
 * This ensures edits persist when switching pages and all changes are tracked globally.
 * Saved updates prevent UI reversion during the brief window between save and refetch.
 *
 * @template TData - The type of data in each row
 */
export function useEditableTableState<TData>({
  initialData,
  keyExtractor,
  editableFields,
}: UseEditableTableStateParams<TData>): UseEditableTableStateReturn<TData> {
  // Store edits: rowKey -> Partial<TData> (only changed fields)
  const [editMap, setEditMap] = useState<Map<string, Partial<TData>>>(
    new Map(),
  );
  // Store original data for edited rows (persists across pages)
  const [originalDataMap, setOriginalDataMap] = useState<Map<string, TData>>(
    new Map(),
  );
  // Store successfully saved rows temporarily (prevents reversion before refetch)
  const [savedUpdates, setSavedUpdates] = useState<Map<string, TData>>(
    new Map(),
  );

  // Track previous initialData to detect when it actually changes (refetch completed)
  const prevInitialDataRef = useRef(initialData);

  // Compute currentData for current page by applying edits to initialData
  // Use savedUpdates as baseline if available (prevents reversion after save)
  const currentData = useMemo(() => {
    return initialData.map((row) => {
      const key = keyExtractor(row);
      const saved = savedUpdates.get(key);
      const baseRow = saved || row;
      const edits = editMap.get(key);
      return edits ? { ...baseRow, ...edits } : baseRow;
    });
  }, [initialData, savedUpdates, editMap, keyExtractor]);

  // Compute all changed rows across ALL pages (not just current page)
  const changedRows = useMemo(() => {
    const rows: TData[] = [];
    editMap.forEach((edits, rowKey) => {
      const original = originalDataMap.get(rowKey);
      if (original) {
        // Reconstruct the edited row
        const editedRow = { ...original, ...edits };
        rows.push(editedRow);
      }
    });
    return rows;
  }, [editMap, originalDataMap]);

  // Count total changed fields across all edited rows
  const totalChangedFields = useMemo(() => {
    let total = 0;
    editMap.forEach((edits, rowKey) => {
      const original = originalDataMap.get(rowKey);
      if (original) {
        // Count how many editable fields were changed
        const changedCount = editableFields.filter(
          (field) =>
            edits[field] !== undefined && edits[field] !== original[field],
        ).length;
        total += changedCount;
      }
    });
    return total;
  }, [editMap, originalDataMap, editableFields]);

  const updateCell = useCallback(
    (rowKey: string, field: keyof TData, value: unknown) => {
      // Store original data if this is the first edit for this row
      const originalRow = initialData.find(
        (row) => keyExtractor(row) === rowKey,
      );
      if (originalRow) {
        setOriginalDataMap((prev) => {
          if (!prev.has(rowKey)) {
            return new Map(prev).set(rowKey, originalRow);
          }
          return prev;
        });
      }

      // Store the edit, or remove it if value matches original
      setEditMap((prev) => {
        const newMap = new Map(prev);
        const existingEdits = newMap.get(rowKey) || {};
        // Use savedUpdates as fallback if originalDataMap is empty (after recent save)
        // This ensures we compare against what was actually saved, not stale initialData
        const original =
          originalDataMap.get(rowKey) ??
          savedUpdates.get(rowKey) ??
          originalRow;

        if (original && value === original[field]) {
          // Value reverted to original — remove this field from edits
          const { [field]: _, ...remainingEdits } = existingEdits as Record<
            string,
            unknown
          >;
          if (Object.keys(remainingEdits).length === 0) {
            newMap.delete(rowKey);
          } else {
            newMap.set(rowKey, remainingEdits as unknown as Partial<TData>);
          }
        } else {
          newMap.set(rowKey, { ...existingEdits, [field]: value });
        }
        return newMap;
      });
    },
    [initialData, keyExtractor, originalDataMap, savedUpdates],
  );

  const isFieldEdited = useCallback(
    (rowKey: string, field: keyof TData): boolean => {
      const edits = editMap.get(rowKey);
      if (!edits) return false;
      return edits[field] !== undefined;
    },
    [editMap],
  );

  const resetChanges = useCallback(() => {
    setEditMap(new Map());
    setOriginalDataMap(new Map());
  }, []);

  const commitSave = useCallback(
    (savedRows: TData[]) => {
      // Store saved rows temporarily to prevent UI reversion
      setSavedUpdates((prev) => {
        const newMap = new Map(prev);
        savedRows.forEach((row) => {
          newMap.set(keyExtractor(row), row);
        });
        return newMap;
      });

      // Clear edit tracking for saved rows, but update originalDataMap with saved values
      // This ensures future edits compare against what was actually saved, not stale data
      const savedKeys = new Set(savedRows.map(keyExtractor));
      setEditMap((prev) => {
        const newMap = new Map(prev);
        savedKeys.forEach((key) => newMap.delete(key));
        return newMap;
      });
      setOriginalDataMap((prev) => {
        const newMap = new Map(prev);
        // Update originalDataMap with the saved values instead of clearing
        savedRows.forEach((row) => {
          newMap.set(keyExtractor(row), row);
        });
        return newMap;
      });
    },
    [keyExtractor],
  );

  // Clear all edits (typically after successful save)
  const setInitialData = useCallback(() => {
    setEditMap(new Map());
    setOriginalDataMap(new Map());
  }, []);

  // Auto-clear savedUpdates and originalDataMap when initialData refreshes with fresh data
  useEffect(() => {
    // Only run cleanup if initialData reference changed (refetch completed)
    if (prevInitialDataRef.current === initialData) {
      return;
    }

    prevInitialDataRef.current = initialData;

    let hasSavedUpdatesToClear = false;
    let hasOriginalDataMapToClear = false;

    if (savedUpdates.size > 0) {
      hasSavedUpdatesToClear = true;
    }

    if (originalDataMap.size > 0) {
      hasOriginalDataMapToClear = true;
    }

    if (!hasSavedUpdatesToClear && !hasOriginalDataMapToClear) {
      return;
    }

    const currentKeys = new Set(initialData.map(keyExtractor));

    if (hasSavedUpdatesToClear) {
      setSavedUpdates((prev) => {
        const newMap = new Map(prev);

        prev.forEach((_, key) => {
          if (currentKeys.has(key)) {
            newMap.delete(key);
          }
        });

        return newMap.size < prev.size ? newMap : prev;
      });
    }

    if (hasOriginalDataMapToClear) {
      setOriginalDataMap((prev) => {
        const newMap = new Map(prev);

        prev.forEach((_, key) => {
          if (currentKeys.has(key)) {
            newMap.delete(key);
          }
        });

        return newMap.size < prev.size ? newMap : prev;
      });
    }
  }, [initialData, keyExtractor, savedUpdates.size, originalDataMap.size]);

  return {
    currentData,
    changedRows,
    totalChangedFields,
    updateCell,
    isFieldEdited,
    resetChanges,
    commitSave,
    setInitialData,
  } as const;
}
